import { GridColumnMenuFilterUIProps } from '@progress/kendo-react-grid';
import { Checkbox, CheckboxChangeEvent, RadioGroup } from '@progress/kendo-react-inputs';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from 'src/components/design-system/Button';
import PopUp from 'src/components/design-system/PopUp';
import Divider from 'src/components/Divider';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import { DropDownListDataItem } from 'src/types/generic';

// Define attribute types matching the backend attribute keys
export type AttributeType =
  | 'isContracted'
  | 'isImageAvailable'
  | 'isSpecialOrder'
  | 'productInFavoriteFolder'
  | 'isOfflineSupplierItem'
  | 'isFormulary'
  | 'hasOldProductId';

export interface AttributeFilterOption {
  type: AttributeType;
  label: string | JSX.Element;
  icon: JSX.Element;
}

const operatorOptions: DropDownListDataItem[] = [
  {
    value: 'and',
    label: 'AND',
  },
  {
    value: 'or',
    label: 'OR',
  },
];

const getDefaultOperator = (operator: string | Function | undefined) => {
  if (typeof operator === 'function') {
    return operator();
  } else if (typeof operator === 'string') {
    return operatorOptions.find((option) => option.value === operator)?.value ?? 'or';
  } else {
    return 'or';
  }
};

interface AttributeFilterUIProps extends GridColumnMenuFilterUIProps {
  attributeOptions: AttributeFilterOption[];
}

const AttributeFilterUI = ({
  attributeOptions,
  ...filterUIProps
}: AttributeFilterUIProps) => {
  const { t } = useTranslation();
  const filterChange = filterUIProps.firstFilterProps.onChange;
  const existingValue = filterUIProps.firstFilterProps.value;
  const operator = getDefaultOperator(filterUIProps.firstFilterProps.operator);

  // Parse existing value (comma-separated string) into Set, or select all by default
  const getInitialSelected = () => {
    if (existingValue && typeof existingValue === 'string' && existingValue.trim() !== '') {
      return new Set<AttributeType>(existingValue.split(',').filter(Boolean) as AttributeType[]);
    }
    // Select all by default
    return new Set<AttributeType>();
  };

  const [selectedAttributes, setSelectedAttributes] =
    useState<Set<AttributeType>>(getInitialSelected);
  const [selectedOperator, setSelectedOperator] = useState<'and' | 'or'>(operator);

  // Calculate if all attributes are selected
  const isAllSelected = useMemo(
    () =>
      attributeOptions.length > 0 &&
      attributeOptions.every((option) => selectedAttributes.has(option.type)),
    [attributeOptions, selectedAttributes]
  );

  // Calculate if some (but not all) attributes are selected
  const isPartialSelected = useMemo(() => {
    const checkedCount = attributeOptions.filter((option) =>
      selectedAttributes.has(option.type)
    ).length;
    return checkedCount > 0 && checkedCount < attributeOptions.length;
  }, [attributeOptions, selectedAttributes]);

  // Set indeterminate state on Select All checkbox
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      // Try multiple selectors to find the checkbox input element
      let selectAllCheckbox = document.getElementById(
        'attr-checkbox-select-all'
      ) as HTMLInputElement;

      // If not found by id, try finding input within element with that id
      if (!selectAllCheckbox || selectAllCheckbox.tagName !== 'INPUT') {
        const container = document.getElementById('attr-checkbox-select-all');
        if (container) {
          selectAllCheckbox = container.querySelector('input[type="checkbox"]') as HTMLInputElement;
        }
      }

      // Fallback: try direct input query
      if (!selectAllCheckbox) {
        selectAllCheckbox = document.querySelector(
          'input[id="attr-checkbox-select-all"]'
        ) as HTMLInputElement;
      }

      if (selectAllCheckbox) {
        selectAllCheckbox.indeterminate = isPartialSelected;
      }
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [isPartialSelected, isAllSelected]);

  // Hide default Clear and Filter buttons
  useEffect(() => {
    const hideDefaultButtons = () => {
      // Multiple selectors to catch all possible button containers
      const selectors = [
        '.k-grid-filter-menu-buttons',
        '.k-filter-menu-buttons',
        '.k-button-group',
      ];

      selectors.forEach((selector) => {
        try {
          const elements = document.querySelectorAll(selector);
          elements.forEach((element) => {
            const htmlElement = element as HTMLElement;
            htmlElement.style.display = 'none';
            htmlElement.style.visibility = 'hidden';
          });
        } catch (e) {
          // Ignore invalid selectors
        }
      });

      // Also check for buttons by text content
      const allButtons = document.querySelectorAll('button.k-button');
      allButtons.forEach((button) => {
        const text = button.textContent?.trim().toLowerCase();
        if (text === 'clear' || text === 'filter') {
          const htmlButton = button as HTMLElement;
          htmlButton.style.display = 'none';
          htmlButton.style.visibility = 'hidden';
        }
      });
    };

    // Hide buttons immediately
    hideDefaultButtons();

    // Use MutationObserver to catch dynamically rendered buttons
    const observer = new MutationObserver(() => {
      hideDefaultButtons();
    });

    // Observe the document body for changes
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Also hide after multiple delays to catch all render cycles
    const timeouts = [
      setTimeout(hideDefaultButtons, 0),
      setTimeout(hideDefaultButtons, 10),
      setTimeout(hideDefaultButtons, 50),
      setTimeout(hideDefaultButtons, 100),
    ];

    return () => {
      observer.disconnect();
      timeouts.forEach((timeout) => clearTimeout(timeout));
    };
  }, []);

  const handleCheckboxChange = (type: AttributeType) => (event: CheckboxChangeEvent) => {
    if (event.syntheticEvent) {
      event.syntheticEvent.stopPropagation();
    }

    setSelectedAttributes((prev: Set<AttributeType>) => {
      const newSet = new Set(prev);

      if (event.value) {
        newSet.add(type);
      } else {
        newSet.delete(type);
      }

      return newSet;
    });
  };

  const handleApply = () => {
    // Create a synthetic event for the filter change
    const syntheticEvent = new Event('apply') as any;

    // Convert Set to comma-separated string
    const commaSeparatedValue = Array.from(selectedAttributes).join(',');
    filterChange({
      value: commaSeparatedValue,
      operator: selectedOperator,
      syntheticEvent,
    });
  };

  const handleClear = () => {
    setSelectedAttributes(new Set<AttributeType>());
    setSelectedOperator('or');
    filterChange({
      value: '',
      operator: 'or',
      syntheticEvent: new Event('clear') as any,
    });
  };

  return (
    <>
      <style>
        {`
          .k-grid-filter-menu-buttons,
          .k-filter-menu-buttons,
          .k-grid-filter-menu-buttons button,
          .k-filter-menu-buttons button {
            display: none !important;
            visibility: hidden !important;
          }
        `}
      </style>
      <div className="flex flex-col">
        <div style={{ padding: '0px 4px' }}>
          <RadioGroup
            data={operatorOptions}
            value={selectedOperator}
            onChange={(e) => setSelectedOperator(e.value)}
            layout="horizontal"
            style={{ gap: '20px', fontSize: '13px' }}
          />
          <Divider />
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            overflowY: 'auto',
            maxHeight: 'min(300px, calc(100vh - 370px))',
          }}
        >
          {attributeOptions.map((option) => (
            <div
              key={option.type}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '0px 4px',
                marginBottom: '4px',
                marginTop: '4px',
              }}
            >
              <Checkbox
                id={`attr-checkbox-${option.type}`}
                value={selectedAttributes.has(option.type)}
                onChange={handleCheckboxChange(option.type)}
                className="-mt-1"
              />
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                  {option.icon}
                </div>
                <label
                  htmlFor={`attr-checkbox-${option.type}`}
                  style={{
                    fontSize: '13px',
                    lineHeight: '1.4',
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                    cursor: 'pointer',
                    flex: 1,
                  }}
                >
                  {option.label}
                </label>
              </div>
            </div>
          ))}
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-start',
            marginTop: '8px',
            alignItems: 'center',
            gap: '8px',
            flexShrink: 0,
          }}
        >
          <Button onClick={handleClear} color="secondary" className="!m-0">
            {t('Clear')}
          </Button>
          <PopUp
            content={
              <div className="m-1 max-w-[300px]">
                <PopUpContentWrapper
                  type="Information"
                  contents={['Select at least one attribute']}
                />
              </div>
            }
            show={selectedAttributes.size === 0}
            anchorAlign={{ horizontal: 'left', vertical: 'bottom' }}
            popupAlign={{ horizontal: 'left', vertical: 'top' }}
          >
            <Button
              onClick={handleApply}
              color="secondary"
              className="!m-0"
              disabled={selectedAttributes.size === 0}
            >
              {t('Apply')}
            </Button>
          </PopUp>
        </div>
      </div>
    </>
  );
};

export default AttributeFilterUI;
